import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, InputBase, List, ListItem, ListItemIcon, ListItemText,
  Box, Typography, Chip, DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Category as ProductIcon,
  Store as SupplierIcon,
  People as CustomerIcon,
  ShoppingCart as POIcon,
  LocalShipping as SOIcon,
  RequestQuote as RFQIcon,
  Description as ContractIcon,
  Inventory as InventoryIcon,
  Assessment as ReportIcon,
  Warehouse as PlantIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

const searchItems = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, keywords: 'home overview kpi' },
  { label: 'Products', path: '/products', icon: <ProductIcon />, keywords: 'product catalog sku item' },
  { label: 'Create Product', path: '/products/new', icon: <ProductIcon />, keywords: 'add new product' },
  { label: 'Suppliers', path: '/suppliers', icon: <SupplierIcon />, keywords: 'vendor supplier partner' },
  { label: 'Add Supplier', path: '/suppliers/new', icon: <SupplierIcon />, keywords: 'create new vendor' },
  { label: 'Customers', path: '/customers', icon: <CustomerIcon />, keywords: 'customer client buyer' },
  { label: 'Plants & Warehouses', path: '/plants', icon: <PlantIcon />, keywords: 'plant warehouse location storage' },
  { label: 'Purchase Orders', path: '/purchase-orders', icon: <POIcon />, keywords: 'po procurement buy order' },
  { label: 'Create Purchase Order', path: '/purchase-orders/new', icon: <POIcon />, keywords: 'new po create purchase' },
  { label: 'Sales Orders', path: '/sales-orders', icon: <SOIcon />, keywords: 'so sales sell ship order' },
  { label: 'Create Sales Order', path: '/sales-orders/new', icon: <SOIcon />, keywords: 'new so create sales' },
  { label: 'Credit Notes', path: '/credit-notes', icon: <SOIcon />, keywords: 'credit note customer return refund' },
  { label: 'Debit Notes', path: '/debit-notes', icon: <POIcon />, keywords: 'debit note supplier return purchase' },
  { label: 'RFQ / Bids', path: '/rfqs', icon: <RFQIcon />, keywords: 'rfq request quote bid tender' },
  { label: 'Contracts', path: '/contracts', icon: <ContractIcon />, keywords: 'contract agreement supplier' },
  { label: 'Inventory', path: '/inventory', icon: <InventoryIcon />, keywords: 'stock inventory level warehouse' },
  { label: 'Goods Receipts', path: '/goods-receipts', icon: <InventoryIcon />, keywords: 'receipt receive goods gr inbound' },
  { label: 'Goods Issues', path: '/goods-issues', icon: <InventoryIcon />, keywords: 'issue dispatch goods gi outbound' },
  { label: 'Transfers', path: '/transfer-postings', icon: <InventoryIcon />, keywords: 'transfer move stock between' },
  { label: 'Physical Inventory', path: '/physical-inventory', icon: <InventoryIcon />, keywords: 'count stocktake physical inventory' },
  { label: 'Inventory Reports', path: '/reports/inventory', icon: <ReportIcon />, keywords: 'report inventory stock valuation' },
  { label: 'Sales Reports', path: '/reports/sales', icon: <ReportIcon />, keywords: 'report sales revenue' },
  { label: 'Procurement Reports', path: '/reports/procurement', icon: <ReportIcon />, keywords: 'report procurement spend supplier' },
  { label: 'Fast-Moving Items', path: '/reports/fast-moving', icon: <ReportIcon />, keywords: 'report fast moving items top selling products' },
  { label: 'Supplier Portal', path: '/supplier-portal/demo', icon: <SupplierIcon />, keywords: 'portal vendor quote submission' },
  { label: 'My Profile', path: '/profile', icon: <SettingsIcon />, keywords: 'profile account settings user' },
  { label: 'Admin Settings', path: '/admin', icon: <SettingsIcon />, keywords: 'admin settings manage users theme' },
  { label: 'Help & Support', path: '/help', icon: <HelpIcon />, keywords: 'help support guide documentation faq workflow shortcuts contact' },
];

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filtered = query.trim()
    ? searchItems.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.keywords.toLowerCase().includes(query.toLowerCase())
      )
    : searchItems.slice(0, 8);

  useEffect(() => { setSelectedIndex(0); }, [query]);
  useEffect(() => { if (open) setQuery(''); }, [open]);

  const handleSelect = useCallback((item) => {
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      handleSelect(filtered[selectedIndex]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed', top: '15%',
          borderRadius: 3, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <SearchIcon sx={{ color: 'text.disabled', fontSize: 22 }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search pages, actions..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: '0.938rem' }}
        />
        <Chip label="ESC" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.625rem', color: 'text.disabled', borderColor: 'divider' }} />
      </Box>

      <List sx={{ py: 0.5, maxHeight: 360, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">No results for "{query}"</Typography>
          </Box>
        ) : (
          filtered.map((item, index) => (
            <ListItem
              key={item.path}
              onClick={() => handleSelect(item)}
              sx={{
                px: 2.5, py: 1,
                cursor: 'pointer',
                bgcolor: index === selectedIndex ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                borderLeft: index === selectedIndex ? '3px solid' : '3px solid transparent',
                borderLeftColor: index === selectedIndex ? 'primary.main' : 'transparent',
                transition: 'all 100ms',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: index === selectedIndex ? 'primary.main' : 'text.disabled' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: index === selectedIndex ? 600 : 400 }}
              />
              {index === selectedIndex && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>Enter ↵</Typography>
              )}
            </ListItem>
          ))
        )}
      </List>
    </Dialog>
  );
};

export default GlobalSearch;
